// Business Logic Layer - pure functions, no React, no side effects. Ported
// verbatim from CompanyFormModal's old inline helpers, just consolidated
// here so the UI layer (index.jsx) and state layer (hooks/useCompanyFormState.js)
// don't carry this logic themselves.

// isAddressSame / shipping-section wiring: the org's "company" object
// customization schema doesn't natively express "there's a same-as-billing
// checkbox" or "hide this section unless it's unchecked" - both are
// injected here. Matched on the section's human-readable title since the
// schema has no stable section id - a pre-existing fragility (carried over
// from the original implementation), not introduced by this rewrite;
// flagged rather than silently "fixed" by guessing an id that may not match
// how individual orgs have actually named this section.
function ensureShippingAddressCondition(sections) {
  return sections.map((section) => {
    if (section.section.toLowerCase().includes("shipping") && !section.showSectionCondition) {
      return {
        ...section,
        showSectionCondition: { field: "isAddressSame", operator: "isFalse", value: "" },
      };
    }
    return section;
  });
}

// Long label - the 2-column grid (see _company-form-modal.scss) would
// otherwise auto-place it beside whichever address field happens to land in
// the previous row's open column.
const IS_ADDRESS_SAME_FIELD = {
  name: "isAddressSame",
  label: "Is billing address same as shipping address?",
  type: "checkbox",
  required: false,
  value: false,
  fullWidth: true,
};

function ensureIsAddressSameField(sections) {
  return sections.map((section) => {
    if (section.section.toLowerCase().includes("billing address")) {
      const hasIsAddressSame = section.fields?.some((field) => field.name === "isAddressSame");
      return {
        ...section,
        fields: hasIsAddressSame
          // Some orgs' stored customization already has this field (added
          // by an earlier pass) without the newer `fullWidth` flag - patch
          // it in either way rather than only on first injection.
          ? section.fields.map((field) =>
              field.name === "isAddressSame" ? { ...field, ...IS_ADDRESS_SAME_FIELD } : field
            )
          : [...section.fields, IS_ADDRESS_SAME_FIELD],
      };
    }
    return section;
  });
}

function disableForBillingPortal(sections) {
  return sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) =>
      field.name === "isIndividual" || field.name === "companyOwner"
        ? { ...field, disabled: true }
        : field
    ),
  }));
}

/**
 * Applies every CompanyFormModal-specific schema post-processing rule to
 * the raw org-customization schema: same-as-billing checkbox injection,
 * shipping-section conditional visibility, and (when opened from the
 * billing portal) disabling isIndividual/companyOwner.
 */
export function processCompanyFormSchema(schema, { isBillingPortal } = {}) {
  let processed = ensureIsAddressSameField(ensureShippingAddressCondition(schema));

  if (isBillingPortal) {
    processed = disableForBillingPortal(processed);
  }

  return processed;
}

/**
 * Derives the create/update success-toast message from a mutation
 * response - same "isIndividual ? individualName : companyName" rule the
 * original inline handlers duplicated once for create and once for edit.
 * Note the two response shapes aren't the same: create's payload is
 * nested under `.data` (createCompany's raw API envelope), edit's isn't
 * (updateCompany/editSelfserveUser already unwrap it) - both are handled
 * here so callers don't need to know the difference.
 */
export function formatCompanySuccessMessage(response, mode) {
  const company = mode === "create" ? response?.data : response;
  const name = company?.isIndividual ? company?.individualName : company?.companyName;
  return `${name} - Company successfully ${mode === "create" ? "created" : "updated"}`;
}
